import { z } from 'zod';

import { type OffsetPaginationRequestQuery } from './pagination';
import { PrimaryKeySchema, commaDelimitedList } from './utils';

// Status enum for pipeline executions
export const PipelineExecutionStatusSchema = z.enum([
  'pending',
  'running',
  'completed',
  'failed',
  'cancelled',
  'paused',
]);

export type PipelineExecutionStatus = z.infer<
  typeof PipelineExecutionStatusSchema
>;

// Progress data schema for real-time updates
export const PipelineExecutionProgressDataSchema = z.object({
  blocks: z.record(
    z.object({
      status: z.enum(['pending', 'running', 'completed', 'failed', 'skipped']),
      progress: z.number().min(0).max(1),
      message: z.string(),
      startedAt: z.string().optional(),
      completedAt: z.string().optional(),
      error: z.string().optional(),
      subTasks: z
        .record(
          z.object({
            progress: z.number().min(0).max(1),
            status: z.string(),
          }),
        )
        .optional(),
      metadata: z.record(z.any()).optional(),
    }),
  ),
  overallProgress: z.number().min(0).max(1),
  currentBlockId: z.string().optional(),
  totalBlocks: z.number().int().nonnegative(),
  completedBlocks: z.number().int().nonnegative(),
});

export type PipelineExecutionProgressData = z.infer<
  typeof PipelineExecutionProgressDataSchema
>;

/**
 * Schema for pipeline executions which store the execution state and progress of pipelines
 */
export const PipelineExecutionSchema = z.object({
  pipelineExecutionId: PrimaryKeySchema,
  pipelineConfigurationVersionId: PrimaryKeySchema,

  // Execution inputs and outputs
  pipelineInput: z
    .record(z.any())
    .describe('The input data provided to the pipeline'),
  pipelineInputSummary: z
    .string()
    .describe('Human-readable summary of the input'),
  pipelineOutput: z
    .record(z.any())
    .nullable()
    .describe('The output produced by the pipeline'),
  pipelineOutputSummary: z
    .string()
    .nullable()
    .describe('Human-readable summary of the output'),

  // Execution state and progress
  status: PipelineExecutionStatusSchema,
  progressData: z
    .record(z.any())
    .describe('Progress information and intermediate results'),
  pipelineOutputFileUri: z
    .string()
    .nullable()
    .describe('S3 URI reference for large pipeline outputs stored in S3'),

  // Execution metrics
  currentStep: z
    .number()
    .int()
    .nonnegative()
    .nullable()
    .describe('Current execution step'),
  totalSteps: z
    .number()
    .int()
    .positive()
    .nullable()
    .describe('Total number of steps in the pipeline'),

  // Timestamps
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  startedAt: z.coerce.date().nullable(),
  completedAt: z.coerce.date().nullable(),
  failedAt: z.coerce.date().nullable(),
  cancelledAt: z.coerce.date().nullable(),
  pausedAt: z.coerce.date().nullable(),
});

export type PipelineExecution = z.infer<typeof PipelineExecutionSchema>;

export const PipelineExecutionsSchema = z.array(PipelineExecutionSchema);

export type PipelineExecutions = z.infer<typeof PipelineExecutionsSchema>;

export const CreatePipelineExecutionSchema = PipelineExecutionSchema.pick({
  pipelineConfigurationVersionId: true,
  pipelineInput: true,
  pipelineInputSummary: true,
  currentStep: true,
  totalSteps: true,
});

export type CreatePipelineExecution = z.infer<
  typeof CreatePipelineExecutionSchema
>;

export const UpdatePipelineExecutionSchema = z.object({
  pipelineOutput: z.record(z.any()).optional(),
  pipelineOutputSummary: z.string().optional(),
  pipelineOutputFileUri: z.string().optional(),
  status: PipelineExecutionStatusSchema.optional(),
  progressData: z.record(z.any()).optional(),
  currentStep: z.number().int().nonnegative().optional(),
  totalSteps: z.number().int().positive().optional(),
  startedAt: z.coerce.date().optional(),
  completedAt: z.coerce.date().optional(),
  failedAt: z.coerce.date().optional(),
  cancelledAt: z.coerce.date().optional(),
  pausedAt: z.coerce.date().optional(),
});

export type UpdatePipelineExecution = z.infer<
  typeof UpdatePipelineExecutionSchema
>;

export const PipelineExecutionsQueryOptionsSchema = z.object({
  pipelineExecutionIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
  pipelineIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
  teamIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
  pipelineConfigurationVersionIds: commaDelimitedList(
    PrimaryKeySchema.array(),
  ).optional(),
  statuses: commaDelimitedList(
    PipelineExecutionStatusSchema.array(),
  ).optional(),
  hasPipelineOutput: z.boolean().optional(),
});

export type PipelineExecutionsQueryOptions = z.infer<
  typeof PipelineExecutionsQueryOptionsSchema
> &
  OffsetPaginationRequestQuery<'createdAt' | 'updatedAt' | 'startedAt'>;
